import { useRef } from 'react';

import { PaidOutBeautyPointsAggregations } from '~api/model/dashboardAggregations';

import { formatNumber, message } from '~helper/_common';

import { useShowDetailsTile } from '~hooks/useShowDetailsTile';

import { BehaviourPointsLabel } from '~components/molecules/BehaviourPointsLabel';
import { LivestreamLabel } from '~components/molecules/LivestreamLabel';
import { ReplaysLabel } from '~components/molecules/ReplaysLabel';
import { BeautyPointsDetailsTile } from '~components/organism/BeautyPointsDetailsTile';
import { DataTile } from '~components/organism/DataTile';
import { DetailsTile } from '~components/organism/DetailsTile';
import { ComponentCommonProps } from '~components/types';

export type PaidOutBeautyPointsDataTileProps = ComponentCommonProps & {
    loading: boolean;
    data: PaidOutBeautyPointsAggregations['paidOutBeautyPoints'] | null;
    creatorId?: string | null;
    hasDetailsTile?: boolean;
}

export const PaidOutBeautyPointsDataTile: React.FC<PaidOutBeautyPointsDataTileProps> = ({
    className,
    data,
    creatorId,
    loading,
    hasDetailsTile,
    'data-test': dataTest = 'paidOutBeautyPointsDataTile',
}) => {
    const dataTileRef = useRef(null);
    const { showDetailsTile, setShowDetailsTile } = useShowDetailsTile();

    return (
        <>
            { showDetailsTile && (
                <DetailsTile
                    dataTileRef={ dataTileRef }
                    onClose={ () => setShowDetailsTile(false) }
                    data-test={ `${dataTest}.details` }
                >
                    <BeautyPointsDetailsTile
                        data={ data }
                        creatorId={ creatorId }
                        mode="paidOutBeautyPoints"
                        data-test={ `${dataTest}.details.beautyPoints` } />
                </DetailsTile>
            ) }
            <DataTile
                prominentMainDataValue
                className={ className }
                contentType="paidOutBeautyPoints"
                headline={ message('dataTile.beautyPoints.headline') }
                headlineSecondary={ message('dataTile.beautyPoints.paidOut') }
                mainDataValue={ message('common.points.short', formatNumber(data?.total || 0)) }
                headlineVariant="h4"
                subHeadlineVariant="h5"
                loading={ loading }
                data-test={ dataTest }
                ref={ dataTileRef }
                onExpandClick={ hasDetailsTile ? () => setShowDetailsTile(true) : undefined }
            >
                <LivestreamLabel
                    loading={ loading }
                    data-test={ `${dataTest}.liveViews` }>
                    { message('common.points.short', formatNumber(data?.liveViews || 0)) }
                </LivestreamLabel>
                <ReplaysLabel
                    loading={ loading }
                    data-test={ `${dataTest}.replayViews` }>
                    { message('common.points.short', formatNumber(data?.replayViews || 0)) }
                </ReplaysLabel>
                <BehaviourPointsLabel
                    loading={ loading }
                    data-test={ `${dataTest}.behaviourPoints` }>
                    { message('common.points.short', formatNumber(data?.behaviourPoints || 0)) }
                </BehaviourPointsLabel>
            </DataTile>
        </>
    );
};
